import {FeatureToggleStatusEnum, IFeatureToggleViewModel} from "./feature-toggle.view-model.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {abTestingFeatureIsValid, getAbFeaturePropertyValue} from "./ab-testing-feature-check";
import {Lazy} from "../../../utils/lazy";
import {IAbTestingStrategy} from "./strategies/ab-testing-strategy.interface";
import {IAbTestingFeature} from "./ab-testing-feature-data-provider.service.interface";
import {Check} from "@sparkware/app-utils";

/**
 * Custom options for FeatureToggleModel
 */
export interface IFeatureToggleModelOptions {
    /**
     * Provide a custom method to evaluate if the feature is enabled by A/B.
     * When provided FeatureToggleModel will use this one instead of using the default featureToggle === true.
     * @param featureData
     */
    isFeatureEnabledByAB?: (featureData: IAbTestingFeature) => boolean;
    getFeatureToggleStatusPerRegulation?: () => Record<number, FeatureToggleStatusEnum>;
}


export class FeatureToggleModel implements IFeatureToggleViewModel {

    constructor(private readonly abFeatureName: string,
                private readonly getFeatureToggleStatus: () => FeatureToggleStatusEnum,
                private readonly getCurrentAbStrategy: () => IAbTestingStrategy,
                private readonly services: IServiceFactoryExtended,
                private readonly options?: IFeatureToggleModelOptions) {
    }

    protected get currentAbStrategy(): IAbTestingStrategy {
        return this.getCurrentAbStrategy();
    }

    public getFeatureABData(): IAbTestingFeature | null {
        return this.currentAbStrategy.getFeature(this.abFeatureName);
    }

    protected _getAbFeatureToggleValue(): boolean {
        const featureData = this.getFeatureABData();
        if(!featureData) {
            return false;
        }

        if(this.options?.isFeatureEnabledByAB) {
            return this.options.isFeatureEnabledByAB(featureData);
        }

        const featureTogglePropertyName = "featuretoggle";
        if (abTestingFeatureIsValid(featureData, [featureTogglePropertyName])) {
            return getAbFeaturePropertyValue(featureData, featureTogglePropertyName)?.toLowerCase() === "true";
        } else {
            this.services.logger.error(`Wrong ${featureTogglePropertyName} property name for ${this.abFeatureName} feature`);
        }

        return false;
    }

    private get isAbEnabled(): boolean {
        return this._getAbFeatureToggleValue();
    }

    get isEnabled(): boolean {

        let toggleStatus: FeatureToggleStatusEnum | null = null;

        if(this.options?.getFeatureToggleStatusPerRegulation) {
            const toggleStatusPerRegulation = this.options.getFeatureToggleStatusPerRegulation();
            if (toggleStatusPerRegulation) {
                toggleStatus = toggleStatusPerRegulation[this.services.regulation.getRegulationCode()];
            }
        }

        if(Check.isNullOrUndefined(toggleStatus)) {
            toggleStatus = this.getFeatureToggleStatus();
        }

        switch (toggleStatus) {
            case FeatureToggleStatusEnum.Disabled:
                return false;
            case FeatureToggleStatusEnum.Enabled:
                return true;
            case FeatureToggleStatusEnum.EnabledByAB:
                return this.isAbEnabled;
            default:
                this.services.logger.error(`Invalid feature toggle config value ${toggleStatus} for ${this.abFeatureName}`);
                return false;

        }
    }
}

export class FeatureToggleModelLazy extends Lazy<FeatureToggleModel> {

}